<script lang="ts">
  /**
   * Модальний filter builder у стилі SP/Airtable з:
   *   - field-picker як portal (виходить за межі модала)
   *   - 3-dot menu на кожній умові: Копіювати / Видалити
   *   - кнопка-pill "АБО"/"І" між groupами і всередині group (toggle)
   *   - до 2 рівнів вкладеності (root group + nested groups)
   *   - інтеграція зі saved filters (передається ззовні)
   */
  import { untrack } from 'svelte';
  import Icon from './Icon.svelte';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import {
    fieldsForScope, operatorsFor, emptyCondition, emptyGroup, isGroup, reassignIds,
    type Condition, type Group, type FieldDef, type Operator,
  } from '../lib/filter-tree';
  import { fieldType as catalogFieldType } from '../lib/data-tables-catalog';
  import {
    type SavedFilter,
    createSavedFilter, deleteSavedFilter,
  } from '../lib/admin-api';

  let {
    open,
    root = $bindable<Group>(),
    onApply,
    onCancel,
    onClear,
    savedFilters = [],
    onSavedChanged,
    onSavedLoad,
    currentSort = null,
    scope = 'persons',
    customFields,
  }: {
    open: boolean;
    root: Group;
    onApply: () => void;
    onCancel: () => void;
    onClear: () => void;
    savedFilters?: SavedFilter[];
    onSavedChanged?: () => void;
    /** Викликається при load saved filter — каллер може застосувати sort. */
    onSavedLoad?: (filter: SavedFilter) => void;
    /** Поточний sort від caller-а — записується разом з filter при saveAs. */
    currentSort?: string | null;
    scope?: string;
    /** Override поля замість fieldsForScope(scope). Для generic custom-tables
     *  сторінки де поля приходять з API. Якщо передано — savedFilters disabled
     *  (бо scope може повторюватись між таблицями і preset-и поплутаються). */
    customFields?: FieldDef[];
  } = $props();

  // ---- field-picker state ----
  // Один глобальний "open" field-picker за раз (можемо ідентифікувати condition
  // об'єкт через посилання). Знаючи координати тригер-кнопки — позиціонуємо
  // dropdown через position:fixed (тому overflow модала не клiпає його).
  type PickerState = {
    cond: Condition;
    rect: DOMRect;
    query: string;
  };
  let picker = $state<PickerState | null>(null);

  // ---- per-row "⋮" menu state ----
  type RowMenuState = { groupRef: Group; idx: number; x: number; y: number };
  let rowMenu = $state<RowMenuState | null>(null);

  // ---- saved filters menu ----
  let showSavedMenu = $state(false);
  let saveAsName = $state('');
  let savingNow = $state(false);

  const FIELDS = $derived(customFields ?? fieldsForScope(scope));

  /** Локалізована назва типу — з єдиного каталогу FIELD_TYPES. */
  function typeLabel(t: FieldDef['type']): string {
    return catalogFieldType(t).label;
  }
  function getField(key: string): FieldDef | undefined {
    return FIELDS.find((f) => f.key === key);
  }
  function filteredFields(q: string): FieldDef[] {
    const s = q.trim().toLowerCase();
    if (!s) return FIELDS;
    return FIELDS.filter(
      (f) => f.label.toLowerCase().includes(s) || f.key.toLowerCase().includes(s),
    );
  }

  function bump() {
    root = { ...root };
  }
  function defaultFieldKey(): string {
    return FIELDS[0]?.key ?? 'displayName';
  }
  function makeDefaultCondition(): Condition {
    const key = defaultFieldKey();
    const fd = getField(key);
    const ops = fd ? operatorsFor(fd.type) : [];
    const c = emptyCondition(key);
    if (ops.length) c.op = ops[0]!.value;
    if (fd?.type === 'checkbox') c.value = undefined;
    return c;
  }
  function addConditionTo(g: Group) {
    g.conditions.push(makeDefaultCondition());
    bump();
  }
  function addGroupTo(g: Group) {
    const ng = emptyGroup();
    ng.conditions.push(makeDefaultCondition());
    g.conditions.push(ng);
    bump();
  }
  /**
   * Гарантуємо що root містить ТІЛЬКИ Group-діти (без direct Conditions).
   * Якщо є прямі — обгортаємо їх у єдину Group на початку. Якщо взагалі
   * немає Group-дітей — додаємо одну порожню.
   */
  function normalizeRoot() {
    const directConds = root.conditions.filter((c) => !isGroup(c)) as Condition[];
    const nestedGroups = root.conditions.filter((c) => isGroup(c)) as Group[];
    if (directConds.length > 0) {
      const wrapper = emptyGroup();
      wrapper.combinator = root.combinator;
      wrapper.conditions = directConds;
      root.conditions = [wrapper, ...nestedGroups];
    }
    if (root.conditions.filter((c) => isGroup(c)).length === 0) {
      const g = emptyGroup();
      g.conditions.push(emptyCondition());
      root.conditions.push(g);
    }
    bump();
  }
  // Викликаємо normalizeRoot ТІЛЬКИ один раз — при переході open false→true.
  // Без untrack ефект би повторно тригерився від мутацій root і утворив би
  // нескінченну петлю.
  let prevOpen = false;
  $effect(() => {
    const isOpen = open;
    if (isOpen && !prevOpen) {
      untrack(() => normalizeRoot());
    }
    prevOpen = isOpen;
  });
  function removeAt(g: Group, idx: number) {
    g.conditions.splice(idx, 1);
    bump();
  }
  function duplicateAt(g: Group, idx: number) {
    const item = g.conditions[idx];
    if (!item) return;
    const clone = JSON.parse(JSON.stringify(item)) as Condition | Group;
    reassignIds(clone); // свіжі _id у клоні + всіх вкладених
    g.conditions.splice(idx + 1, 0, clone);
    bump();
  }
  function setCondField(c: Condition, key: string) {
    c.field = key;
    const fd = getField(key);
    const ops = fd ? operatorsFor(fd.type) : [];
    c.op = ops[0]?.value ?? 'eq';
    c.value = fd?.type === 'checkbox' ? undefined : '';
    bump();
  }
  function setCondOp(c: Condition, op: Operator) {
    c.op = op;
    if (op === 'between') c.value = ['', ''];
    if (op === 'has_value' || op === 'has_no_value' || op === 'is_true' || op === 'is_false') {
      c.value = undefined;
    }
    bump();
  }
  function toggleCombinator(g: Group) {
    g.combinator = g.combinator === 'AND' ? 'OR' : 'AND';
    bump();
  }
  function combinatorLabel(c: 'AND' | 'OR'): string {
    return c === 'AND' ? 'І' : 'АБО';
  }
  /** 'І' — синій; 'АБО' — зелений. */
  function combinatorClass(c: 'AND' | 'OR'): string {
    return c === 'AND'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  function openPicker(c: Condition, e: MouseEvent) {
    const target = e.currentTarget as HTMLElement;
    picker = { cond: c, rect: target.getBoundingClientRect(), query: '' };
  }
  function pickField(key: string) {
    if (picker) {
      setCondField(picker.cond, key);
      picker = null;
    }
  }
  function openRowMenu(g: Group, idx: number, e: MouseEvent) {
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const r = target.getBoundingClientRect();
    rowMenu = { groupRef: g, idx, x: r.right - 160, y: r.bottom + 4 };
  }

  async function doSaveAs() {
    if (!saveAsName.trim()) return;
    savingNow = true;
    try {
      await createSavedFilter(scope, saveAsName.trim(), root, currentSort);
      saveAsName = '';
      onSavedChanged?.();
    } finally {
      savingNow = false;
    }
  }
  function loadSaved(sf: SavedFilter) {
    const tree = JSON.parse(JSON.stringify(sf.tree)) as Group;
    reassignIds(tree); // saved tree не має _id — генеруємо
    root = tree;
    showSavedMenu = false;
    // Повідомляємо caller-а щоб застосував sort з preset (якщо передбачений).
    onSavedLoad?.(sf);
  }
  async function removeSaved(sf: SavedFilter) {
    if (!confirm(`Видалити збережений фільтр "${sf.name}"?`)) return;
    await deleteSavedFilter(sf.id);
    onSavedChanged?.();
  }
</script>

{#if open}
  <div class="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onclick={onCancel} role="presentation">
    <div
      class="w-[min(900px,95vw)] max-h-[85vh] overflow-hidden flex flex-col rounded-xl bg-white shadow-2xl relative"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
    >
      <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
        <div class="text-lg font-bold">Фільтр</div>
        <div class="flex items-center gap-2">
          <!-- Saved filters dropdown -->
          <div class="relative">
            <button
              onclick={() => (showSavedMenu = !showSavedMenu)}
              class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
            >
              <Icon name="star" size={13} /> Збережені
              {#if savedFilters.length > 0}
                <span class="text-[10px] text-[var(--color-text-muted)]">({savedFilters.length})</span>
              {/if}
            </button>
            {#if showSavedMenu}
              <div class="absolute right-0 top-full mt-1 z-50 w-[280px] rounded-md border border-[var(--color-border-3)] bg-white shadow-xl">
                <div class="border-b border-[var(--color-border-3)] p-2">
                  <div class="text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-1.5">Зберегти поточний</div>
                  <div class="flex gap-1">
                    <input
                      bind:value={saveAsName}
                      placeholder="Назва фільтра"
                      class="flex-1 rounded border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                    />
                    <button
                      onclick={doSaveAs}
                      disabled={!saveAsName.trim() || savingNow}
                      class="rounded bg-[var(--color-pu)] px-2 py-1 text-[10px] font-semibold text-white disabled:opacity-50"
                    >{savingNow ? '…' : 'Зберегти'}</button>
                  </div>
                </div>
                <div class="max-h-[260px] overflow-y-auto">
                  {#if savedFilters.length === 0}
                    <div class="px-3 py-4 text-center text-[11px] text-[var(--color-text-muted)] italic">
                      Поки немає збережених
                    </div>
                  {:else}
                    {#each savedFilters as sf (sf.id)}
                      <div class="group flex items-center gap-1 px-2 py-1.5 hover:bg-[var(--color-bg-3)]">
                        <button onclick={() => loadSaved(sf)} class="flex-1 text-left text-xs hover:underline truncate">
                          {sf.name}
                        </button>
                        <button onclick={() => removeSaved(sf)} class="inline-flex items-center text-red-600 opacity-0 group-hover:opacity-100" title="Видалити"><Icon name="close" size={12} /></button>
                      </div>
                    {/each}
                  {/if}
                </div>
              </div>
            {/if}
          </div>
          <button onclick={onCancel} class="inline-flex items-center leading-none text-[var(--color-text-muted)] hover:text-[var(--color-text)] px-1.5" aria-label="Закрити"><Icon name="close" size={18} /></button>
        </div>
      </div>

      <div class="overflow-y-auto p-5 flex-1">
        <div class="text-[12px] text-[var(--color-text-muted)] mb-4">
          Виберіть умови для фільтрування за…
        </div>

        {#snippet renderGroup(g: Group, depth: number)}
          <div class="rounded-lg border border-[var(--color-border-3)] p-3">
            <!-- Спершу — усі прості умови (Conditions) у поточній групі. -->
            {#each g.conditions as item, idx (item._id ?? idx)}
              {#if !isGroup(item)}
                {@const fd = getField(item.field)}
                {@const ops = fd ? operatorsFor(fd.type) : []}
                {@const condIdx = g.conditions.slice(0, idx).filter((x) => !isGroup(x)).length}
                <div class="grid grid-cols-[90px_minmax(180px,1fr)_140px_minmax(140px,1fr)_30px] items-center gap-2 py-1">
                  <div class="text-[11px] text-[var(--color-text-muted)] {condIdx > 0 ? 'pl-2' : ''}">
                    {#if condIdx === 0}
                      Фільтрув…
                    {:else}
                      <button
                        onclick={() => toggleCombinator(g)}
                        class="rounded-md border px-2 py-0.5 text-[10px] font-bold {combinatorClass(g.combinator)}"
                      >{combinatorLabel(g.combinator)}</button>
                    {/if}
                  </div>

                  <!-- Field picker trigger -->
                  <button
                    onclick={(e) => openPicker(item, e)}
                    class="w-full text-left rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 text-xs flex items-center justify-between"
                  >
                    <span class="truncate">{fd?.label ?? 'Оберіть поле'}</span>
                    <span class="ml-2 inline-flex text-[var(--color-text-muted)]"><Icon name="chevronDown" size={13} /></span>
                  </button>

                  <!-- Operator -->
                  <select
                    value={item.op}
                    onchange={(e) => setCondOp(item, (e.currentTarget as HTMLSelectElement).value as Operator)}
                    class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1.5 text-xs"
                  >
                    {#each ops as op}
                      <option value={op.value}>{op.label}</option>
                    {/each}
                  </select>

                  <!-- Value input(s) -->
                  <div>
                    {#if item.op === 'has_value' || item.op === 'has_no_value' || item.op === 'is_true' || item.op === 'is_false'}
                      <!-- значення не потрібне — пусто -->
                    {:else if item.op === 'between'}
                      {@const arr = Array.isArray(item.value) ? (item.value as unknown[]) : ['', '']}
                      <div class="grid grid-cols-2 gap-1">
                        <input
                          type={fd?.type === 'date' ? 'date' : 'number'}
                          value={String(arr[0] ?? '')}
                          oninput={(e) => { const a = [...arr]; a[0] = (e.currentTarget as HTMLInputElement).value; item.value = a; bump(); }}
                          placeholder="від"
                          class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                        />
                        <input
                          type={fd?.type === 'date' ? 'date' : 'number'}
                          value={String(arr[1] ?? '')}
                          oninput={(e) => { const a = [...arr]; a[1] = (e.currentTarget as HTMLInputElement).value; item.value = a; bump(); }}
                          placeholder="до"
                          class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                        />
                      </div>
                    {:else if fd?.type === 'dropdown'}
                      <select
                        value={String(item.value ?? '')}
                        onchange={(e) => { item.value = (e.currentTarget as HTMLSelectElement).value; bump(); }}
                        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                      >
                        <option value="">— виберіть —</option>
                        {#each fd.enumOptions ?? [] as opt}
                          <option value={opt.value}>{opt.label}</option>
                        {/each}
                      </select>
                    {:else if fd?.type === 'date'}
                      <input
                        type="date"
                        value={String(item.value ?? '')}
                        oninput={(e) => { item.value = (e.currentTarget as HTMLInputElement).value; bump(); }}
                        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                      />
                    {:else if fd && (fd.type === 'number' || fd.type === 'auto_number' || fd.type === 'percent' || fd.type === 'currency' || fd.type === 'stat')}
                      <input
                        type="number"
                        value={String(item.value ?? '')}
                        oninput={(e) => { item.value = (e.currentTarget as HTMLInputElement).value; bump(); }}
                        placeholder={fd.type === 'currency' ? '₴' : fd.type === 'percent' ? '%' : ''}
                        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                      />
                    {:else}
                      <input
                        type="text"
                        value={String(item.value ?? '')}
                        oninput={(e) => { item.value = (e.currentTarget as HTMLInputElement).value; bump(); }}
                        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
                      />
                    {/if}
                  </div>

                  <!-- 3-dot row menu -->
                  <button
                    onclick={(e) => openRowMenu(g, idx, e)}
                    class="inline-flex items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-text)] leading-none"
                    title="Дії"
                  ><Icon name="more" size={16} /></button>
                </div>
              {/if}
            {/each}

            <!-- + Додати умову — належить ЦЕЙ групі, тому всередині її border'а -->
            <div class="pt-2 pl-2 flex items-center gap-3">
              <button onclick={() => addConditionTo(g)} class="text-[11px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:underline">
                + Додати умову
              </button>
            </div>
          </div>
        {/snippet}

        <!-- Root рендериться ОСОБЛИВО: без власної рамки. Кожна вкладена
             група — окремий box. Між групами centered combinator pill —
             це root.combinator (окремо від внутрішніх group.combinator). -->
        {#each root.conditions as child, idx (child._id ?? idx)}
          {#if isGroup(child)}
            {#if idx > 0}
              <div class="flex justify-center my-3">
                <button
                  onclick={() => toggleCombinator(root)}
                  class="inline-flex items-center gap-1 rounded-md border px-3 py-0.5 text-[11px] font-bold {combinatorClass(root.combinator)}"
                >{combinatorLabel(root.combinator)} <Icon name="chevronDown" size={12} /></button>
              </div>
            {/if}
            {@render renderGroup(child, 1)}
          {/if}
        {/each}

        <div class="mt-4 flex items-center justify-between">
          <button onclick={() => addGroupTo(root)} class="text-[11px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:underline">
            + Додати групу
          </button>
          <button onclick={onClear} class="text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] hover:text-red-600 font-semibold">
            Очистити фільтри
          </button>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
        <button onclick={onCancel} class="rounded-md border border-[var(--color-border-3)] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          Скасувати
        </button>
        <button onclick={onApply} class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white">
          Застосувати
        </button>
      </div>
    </div>
  </div>

  <!-- ──────────── Field picker portal (fixed-positioned, outside modal) ──────────── -->
  {#if picker}
    <div class="fixed inset-0 z-50" onclick={() => (picker = null)} role="presentation">
      <div
        class="absolute rounded-md border border-[var(--color-border-3)] bg-white shadow-2xl max-h-[320px] overflow-y-auto"
        style="left: {Math.max(8, Math.min(window.innerWidth - 308, picker.rect.left))}px; top: {Math.min(window.innerHeight - 340, picker.rect.bottom + 4)}px; width: 300px;"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div class="sticky top-0 bg-white border-b border-[var(--color-border-3)] p-2">
          <input
            bind:value={picker.query}
            placeholder="Пошук поля…"
            autofocus
            class="w-full rounded border border-[var(--color-border-3)] bg-white px-2 py-1 text-xs"
          />
        </div>
        {#each filteredFields(picker.query) as f (f.key)}
          <button
            onclick={() => pickField(f.key)}
            class="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[var(--color-bg-3)]"
            class:bg-purple-50={picker.cond.field === f.key}
          >
            <span class="grid h-6 w-6 shrink-0 place-items-center rounded text-[var(--color-pu-3)]">
              <FieldTypeIcon type={f.type} size={15} />
            </span>
            <span class="flex-1 truncate text-[13px]">{f.label}</span>
            <span class="text-[12px] text-[var(--color-text-muted)]">{typeLabel(f.type)}</span>
          </button>
        {:else}
          <div class="px-3 py-3 text-center text-[12px] text-[var(--color-text-muted-2)]">Нічого не знайдено</div>
        {/each}
      </div>
    </div>
  {/if}

  <!-- ──────────── Row 3-dot menu ──────────── -->
  {#if rowMenu}
    <div class="fixed inset-0 z-50" onclick={() => (rowMenu = null)} role="presentation">
      <div
        class="absolute rounded-md border border-[var(--color-border-3)] bg-white shadow-xl min-w-[160px]"
        style="left: {Math.max(8, rowMenu.x)}px; top: {Math.min(window.innerHeight - 100, rowMenu.y)}px;"
        onclick={(e) => e.stopPropagation()}
        role="menu"
      >
        <button
          onclick={() => { if (rowMenu) duplicateAt(rowMenu.groupRef, rowMenu.idx); rowMenu = null; }}
          class="flex items-center gap-2 w-full text-left px-3 py-2 text-xs hover:bg-[var(--color-bg-3)]"
        ><Icon name="copy" size={14} /> Копіювати</button>
        <button
          onclick={() => { if (rowMenu) removeAt(rowMenu.groupRef, rowMenu.idx); rowMenu = null; }}
          class="flex items-center gap-2 w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
        ><Icon name="delete" size={14} /> Видалити</button>
      </div>
    </div>
  {/if}
{/if}
